/**
 * Favorites Manager for Color Picker
 * Handles storage and management of favorite colors
 */

const STORAGE_KEY = 'giotto-color-picker-favorites';
const MAX_FAVORITES = 20;

/**
 * Color Favorites Manager class
 */
export class FavoritesManager {
  constructor() {
    this.favorites = this.loadFavorites();
    this.listeners = new Set();
  }

  /**
   * Load favorites from localStorage
   * @returns {string[]} Array of favorite color strings
   */
  loadFavorites() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Errore nel caricamento dei colori preferiti:', error);
      return [];
    }
  }

  /**
   * Save favorites to localStorage
   */
  saveFavorites() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.favorites));
      this.notifyListeners();
    } catch (error) {
      console.warn('Errore nel salvataggio dei colori preferiti:', error);
    }
  }

  /**
   * Get all favorite colors
   * @returns {string[]} Array of favorite color strings
   */
  getFavorites() {
    return [...this.favorites];
  }

  /**
   * Add a color to favorites
   * @param {string} color - Color string to add
   * @returns {boolean} True if added successfully
   */
  addFavorite(color) {
    if (!color || this.isFavorite(color)) {
      return false;
    }

    // Add to beginning of array
    this.favorites.unshift(color);

    // Keep only max favorites
    if (this.favorites.length > MAX_FAVORITES) {
      this.favorites = this.favorites.slice(0, MAX_FAVORITES);
    }

    this.saveFavorites();
    return true;
  }

  /**
   * Remove a color from favorites
   * @param {string} color - Color string to remove
   * @returns {boolean} True if removed successfully
   */
  removeFavorite(color) {
    const index = this.favorites.indexOf(color);
    if (index === -1) {
      return false;
    }

    this.favorites.splice(index, 1);
    this.saveFavorites();
    return true;
  }

  /**
   * Check if a color is in favorites
   * @param {string} color - Color string to check
   * @returns {boolean} True if color is favorited
   */
  isFavorite(color) {
    return this.favorites.includes(color);
  }

  /**
   * Toggle favorite status of a color
   * @param {string} color - Color string to toggle
   * @returns {boolean} New favorite status
   */
  toggleFavorite(color) {
    if (this.isFavorite(color)) {
      this.removeFavorite(color);
      return false;
    } else {
      this.addFavorite(color);
      return true;
    }
  }

  /**
   * Clear all favorites
   */
  clearFavorites() {
    this.favorites = [];
    this.saveFavorites();
  }

  /**
   * Get the maximum number of favorites allowed
   * @returns {number} Maximum favorites count
   */
  getMaxFavorites() {
    return MAX_FAVORITES;
  }

  /**
   * Check if favorites limit is reached
   * @returns {boolean} True if at limit
   */
  isAtLimit() {
    return this.favorites.length >= MAX_FAVORITES;
  }

  /**
   * Add a listener for favorites changes
   * @param {Function} callback - Callback function
   */
  addListener(callback) {
    this.listeners.add(callback);
  }

  /**
   * Remove a listener for favorites changes
   * @param {Function} callback - Callback function
   */
  removeListener(callback) {
    this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of changes
   */
  notifyListeners() {
    this.listeners.forEach((callback) => {
      try {
        callback(this.favorites);
      } catch (error) {
        console.warn('Errore nel listener dei preferiti:', error);
      }
    });
  }

  /**
   * Import favorites from an array
   * @param {string[]} colors - Array of color strings
   * @returns {number} Number of colors imported
   */
  importFavorites(colors) {
    if (!Array.isArray(colors)) {
      return 0;
    }

    const validColors = colors.filter(
      (color) => typeof color === 'string' && color.trim()
    );

    this.favorites = validColors.slice(0, MAX_FAVORITES);
    this.saveFavorites();

    return this.favorites.length;
  }

  /**
   * Export favorites as array
   * @returns {string[]} Array of favorite colors
   */
  exportFavorites() {
    return [...this.favorites];
  }
}

// Create singleton instance
export const favoritesManager = new FavoritesManager();
